import {ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, OnInit} from '@angular/core';
import {SpinnerLoaderService} from './core/spinner-loader.service';
import {environment} from '../environments/environment';
import {AuthService} from './core/auth.service';
import {GlobalConstants} from './shared/global-constants';
import {CommonService} from './shared/common.service';
import {TokenService} from './core/token.service';
import {AppInitializerService} from './core/app-initializer.service';
import {tap} from 'rxjs/operators';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit {
  title = 'LMS-Theseus-UI';
  loadSpinner: boolean;
  loadProgressBar: boolean;
  copyrightMsg: any;
  refreshIntervalForTokenTimeOut: ReturnType<typeof setInterval>;
  refreshIntervalForTabClose: ReturnType<typeof setInterval>;
  tokenCheckInterval: ReturnType<typeof setInterval>;
  IDLE_TIMEOUT = 3600; // seconds
  idleSecondsCounter = 0;
  showUI = false;

  constructor(
    private readonly loader: SpinnerLoaderService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly authService: AuthService,
    private readonly service: CommonService,
    private readonly tokenService: TokenService,
    private readonly appInitializerService: AppInitializerService
  ) {
    this.loader.isLoading.subscribe((isLoading) => {
      this.loadSpinner = isLoading;
      this.cdRef.markForCheck();
    });

    this.loader.isProgressBar.subscribe((isProgressBar) => {
      this.loadProgressBar = isProgressBar;
      this.cdRef.markForCheck();
    });
  }

  ngOnInit(): void {
    this.appInitializerService.initConfig()
      .pipe(
        tap(() => this.checkIdle()),
        tap(() => this.setIntervalForTokenCheck()),
      )
      .subscribe();
    this.showUI = true;
  }

  setIntervalForTokenCheck(): void {
    this.tokenCheckHandler();
    this.tokenCheckInterval = setInterval(() => this.tokenCheckHandler(), 1000);
    this.getCopyrightInfo();
    this.setRefreshTokenIterval();
  }

  private tokenCheckHandler = () => {
    if (!this.tokenService.hasToken()) {
      this.showUI = false;
      this.clearIntervalsAndLS();
      window.open(environment.redirectUrl, '_self');
    }
  }

  setRefreshTokenIterval(): void {
    // set interval to refresh token after 50 min
    this.refreshIntervalForTokenTimeOut = setInterval(() => {
      this.authService
        .refreshToken(`${environment.v2ApiBaseUrl}${environment.refreshToken}`)
        .subscribe(
          (res) => {
            if (GlobalConstants.TOKEN_EXPIRE_ERROR_CODE === res.code || !res.token) {
              this.onRefreshTokenError();
              return;
            }
            this.tokenService.setToken(res.token);
            localStorage.setItem(GlobalConstants.REFRESH_TIME, this.service.encrypt(`${Date.now() + GlobalConstants.REFRESH_TIME_VALUE}`));
            localStorage.setItem(GlobalConstants.LOGIN_TIME, this.service.encrypt(`${Date.now()}`));
          },
          () => {
            this.showUI = false;
            this.clearIntervalsAndLS();
            localStorage.setItem(GlobalConstants.REDIRECT_URL, environment.redirectUrl);
            window.open(
              `${window.location.origin}/assets/logout.html`,
              '_self'
            );
          }
        );
    }, GlobalConstants.REFRESH_TIME_VALUE);

    // Interval for refresh token check after refresh or tab close
    this.refreshIntervalForTabClose = setInterval(() => {
      if (this.service.decrypt(localStorage.getItem(GlobalConstants.REFRESH_TIME))) {
        if (Date.now() > Number(this.service.decrypt(localStorage.getItem(GlobalConstants.REFRESH_TIME)))) {
          this.authService
            .refreshToken(
              `${environment.v2ApiBaseUrl}${environment.refreshToken}`
            )
            .subscribe({
              next: res => {
                if (GlobalConstants.TOKEN_EXPIRE_ERROR_CODE === res.code || !res.token) {
                  this.onRefreshTokenError();
                  return;
                }
                this.tokenService.setToken(res.token);
                localStorage.setItem(GlobalConstants.LOGIN_TIME, this.service.encrypt(`${''}${Date.now()}`));
                localStorage.setItem(
                    GlobalConstants.REFRESH_TIME,
                    this.service.encrypt(`${''}${Date.now() + GlobalConstants.REFRESH_TIME_VALUE}`)
                );
              },
              error: () => this.onRefreshTokenError(),
            }
          );
        }
      }
    }, 3000);
  }

  private onRefreshTokenError = () => {
    this.showUI = false;
    this.clearIntervalsAndLS();
    localStorage.setItem(GlobalConstants.REDIRECT_URL, environment.redirectUrl);
    window.open(
        `${window.location.origin}/assets/logout.html`,
        '_self'
    );
  }

  getCopyrightInfo(): void {
    const currYear = new Date().getFullYear();
    this.copyrightMsg = `&#169; ${currYear} Leica Microsystems`;
  }

  @HostListener('document:click', ['$event'])
  documentClick(): void {
    this.idleSecondsCounter = 0;
  }

  @HostListener('document:mousemove', ['$event'])
  documentMouseMove(): void {
    this.idleSecondsCounter = 0;
  }

  @HostListener('document:keypress', ['$event'])
  documentKeyPress(): void {
    this.idleSecondsCounter = 0;
  }

  checkIdle(): void {
    setInterval(() => {
      this.idleSecondsCounter++;
      if (this.idleSecondsCounter >= this.IDLE_TIMEOUT) {
        this.showUI = false;
        this.clearIntervalsAndLS();
        localStorage.setItem(GlobalConstants.REDIRECT_URL, environment.redirectUrl);
        window.open(`${window.location.origin}/assets/logout.html`, '_self');
      }
    }, 1000);
  }

  clearIntervalsAndLS(): void {
    if (this.refreshIntervalForTokenTimeOut) {
      clearInterval(this.refreshIntervalForTokenTimeOut);
    }
    if (this.refreshIntervalForTabClose) {
      clearInterval(this.refreshIntervalForTabClose);
    }
    if (this.tokenCheckInterval) {
      clearInterval(this.tokenCheckInterval);
    }
    localStorage.clear();
  }
}
